import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
  AbstractControl,
  FormControl,
  NgForm,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  AppConfigService,
  CustomValidators,
  DictionaryBaseDto,
  FormDialogComponent,
  GrowlService,
  InquiryService,
  LoggedUserService,
  OfferService,
  PolicyCreationDataDto,
  PolicyInquiryOfferDto,
  PolicyInquiryOfferEnvelopeDto,
  RouterService,
  StringUtils,
  TemplateService,
  TemplateSimpleDto,
  UserGroupBaseDto,
  UserService,
  VoteDto,
} from '../../bonding_shared';
import {DetailsView} from '../../bonding_shared/components/details-view/details-view';
import {Observable, Subscription} from 'rxjs';
import {
  BusinessObjectType,
  DictionaryProfile,
  DocumentType,
  FieldValueType,
  InquiryProductType,
  InquiryProductTypes,
  LimitedLiability,
  PolicyElementaryRight,
  PolicyInquiryOfferStatus,
  PolicyInquiryOfferVotingStatus,
} from '../../bonding_shared/model/dictionary-ids';
import {map, tap} from 'rxjs/operators';
import * as _ from 'lodash';
import {votingGroupFilter} from './domain/offer-allowance';
import {PolicyContractViewSection, PolicyContractViewService} from '../policy/services/policy-contract-view.service';
import {getInquiryVersion} from './domain/offer-common';
import {TranslateService} from '@ngx-translate/core';
import {BooleanPair} from '../../bonding_shared/components/form-row/boolean-pair-radio-row.component';
import {Button} from '../../bonding_shared/components/details-view/button';

@Component({
  selector: 'offer-details',
  templateUrl: 'offer-details.component.html',
  styles: [
    '.vote-users {display: inline-block; padding-left: 0; width: calc(100% - 220px);}',
    '.vote-users li {display: inline-block; padding-right: 25px;}',
    '.user-comment {font-style: italic; display: inline-block; margin-bottom: 1.2em;}',
    '.user-name {font-size: 1.2em; display:block}',
  ],
})
export class OfferDetailsComponent extends DetailsView implements OnInit, OnDestroy {
  public pending: boolean;
  readonly PolicyElementaryRight = PolicyElementaryRight;
  readonly DictionaryProfile = DictionaryProfile;

  offerEnvelope: PolicyInquiryOfferEnvelopeDto;
  voteCommentControl: UntypedFormControl;
  public inquiryId: number;
  public offerId: number;
  private offerTypeId: number;
  private sourceOfferId: number;
  private blogOffer = false;
  private paramSubs: Subscription;
  templates: TemplateSimpleDto[] = [];
  userGroups: Observable<UserGroupBaseDto[]>;
  readonly viewSection = PolicyContractViewSection;
  public bonusMalusSectionLabelKey: string;

  @ViewChild(FormDialogComponent, {static: true}) votingDialog: FormDialogComponent;
  @ViewChild('ngForm', {static: true}) ngForm: NgForm;
  readonly newContractActions = [
    {key: 'POLICY_CREATED', name: 'bondingContractOffer.details.button.newPolicy'},
    {key: 'POLICY_CREATED_EXTERNALLY', name: 'bondingContractOffer.details.button.markCreatedExternally'},
  ];

  constructor(
    private route: ActivatedRoute,
    public routerService: RouterService,
    private inquiryService: InquiryService,
    protected translateService: TranslateService,
    public router: RouterService,
    protected growlService: GrowlService,
    protected loggedUserService: LoggedUserService,
    protected templateService: TemplateService,
    protected userService: UserService,
    public offerService: OfferService,
    public appConfig: AppConfigService,
    private policyContractViewService: PolicyContractViewService
  ) {
    super(growlService);

    this.hideButtons(false);
    this.deleteButton.hidden = true;
    this.newVersionButton.hidden = true;

    this.form = new UntypedFormGroup({
      insurancePeriod: new UntypedFormControl('', Validators.required),
      invoicedPremium: new UntypedFormControl(''),
    });

    this.form.addControl(
      'annualLOL',
      new UntypedFormControl(
        '',
        this.form.get('globalLOL') ? CustomValidators.lessEqThanOtherField('globalLOL') : undefined
      )
    );
    this.form.addControl('premium', new UntypedFormControl(''));
    this.form.get('premium').setValidators(Validators.required);
    this.form.addControl('premiumRate', new UntypedFormControl('', Validators.max(100)));
    this.form.addControl('nql', new UntypedFormControl('', this.requiredNql()));
    this.form
      .get('invoicedPremium')
      .setValidators(
        Validators.compose([Validators.required, CustomValidators.maxValue(() => this.form.get('premium').value)])
      );

    this.voteCommentControl = new UntypedFormControl('');
  }

  ngOnInit() {
    this.readParams();
  }

  readParams() {
    this.paramSubs = this.route.params.subscribe((params) => {
      const offerId = +params['offerId'];
      if (offerId === this.offerId) {
        return;
      }
      if (+params['inquiryId'] > 0) {
        this.inquiryId = +params['inquiryId'];
      }
      if (offerId > 0) {
        this.offerId = offerId;
      } else {
        this.offerId = 0;
      }
      if (+params['offerTypeId'] > 0) {
        this.offerTypeId = +params['offerTypeId'];
      } else {
        this.offerTypeId = 0;
      }
      if (+params['sourceOfferId'] > 0) {
        this.sourceOfferId = +params['sourceOfferId'];
      } else {
        this.sourceOfferId = 0;
      }
      this.blogOffer = params['blogOffer'] === 'true';

      this.initOfferField();
    });
  }

  getUserGroups(): void {
    const groupFilter = votingGroupFilter(this.offerEnvelope.offer.type);

    this.userGroups = this.userService.getUserGroups().pipe(map((groups) => groups.filter(groupFilter)));
  }

  ngOnDestroy(): void {
    this.paramSubs.unsubscribe();
  }

  onSave(voteTask = false) {
    this.sendOffer(voteTask, (vt) => this.inquiryService.saveOffer(this.updateModel(), vt));
  }

  private updateModel() {
    const formRawValue = this.form.getRawValue();
    _.assign(this.offerEnvelope.offer, formRawValue);
    return this.offerEnvelope.offer;
  }

  private sendOffer(voteTask: boolean, func: (voteTask: boolean) => Observable<PolicyInquiryOfferEnvelopeDto>) {
    this.serverErrors = undefined;
    this.voteCommentControl.setErrors(null);
    this.showErrors = true;
    if (!this.form.valid || !this.ngForm.form.valid) {
      this.showFormError();
      StringUtils.logFormInvalidFields(!this.form.valid ? this.form : this.ngForm.form);
    } else {
      this.inProgress = true;
      func(voteTask).subscribe({
        next: (offerEnvelope) => {
          this.offerEnvelope = offerEnvelope;
          this.growlService.notice('Offer saved!');
          if (offerEnvelope.offer.warnings?.length > 0) {
            this.growlService.warning(offerEnvelope.offer.warnings.map((e) => e.message).join('\n'));
          }
          this.routerService.toInquiryOfferDetails(this.inquiryId, this.offerEnvelope.offer.id);
        },
        error: (error) => {
          voteTask = false;
          if (this.offerEnvelope?.offer?.voting) {
            this.offerEnvelope.offer.voting.userGroup = undefined as any;
          }
          this.handleServerError(error);
        },
        complete: () => (this.inProgress = false),
      });
    }
  }

  onCancel(route?: ActivatedRoute) {
    super.onCancel();
    this.routerService.toInquiryDetails(this.getInquiry());
  }

  private initOfferField() {
    this.offerEnvelope = <PolicyInquiryOfferEnvelopeDto>{offer: <PolicyInquiryOfferDto>{votes: {}}};
    this.inProgress = true;
    if (this.offerId === 0) {
      this.inquiryService.initOffer(this.inquiryId, this.offerTypeId, this.sourceOfferId, this.blogOffer).subscribe({
        next: (offer) => this.handleGetOffer(offer),
        error: (error) => this.handleServerError(error),
        complete: () => (this.inProgress = false),
      });
    } else {
      this.inquiryService.getOffer(this.offerId).subscribe({
        next: (offer) => this.handleGetOffer(offer),
        error: (error) => this.handleServerError(error),
        complete: () => (this.inProgress = false),
      });
    }
  }

  updateControls() {
    if (!this.form.get('aad') && this.isSectionVisible(this.viewSection.OFFER__AAD, true)) {
      this.form.addControl('aad', new UntypedFormControl('', Validators.required));
    }
    if (!this.form.get('globalLOL')) {
      this.form.addControl('globalLOL', new UntypedFormControl('', Validators.required));
    }
    if (this.isSectionVisible(this.viewSection.OFFER__INSURANCE_COVER)) {
      this.form.addControl(
        'insuranceCover',
        new FormControl<number>(
          null,
          Validators.compose([Validators.required, Validators.min(0.01), Validators.max(100)])
        )
      );
    }
  }

  private handleGetOffer(envelope: PolicyInquiryOfferEnvelopeDto) {
    this.offerEnvelope = envelope;
    this.updateControls();
    this.onInquiryTypeChanged();
    this.form.patchValue(this.offerEnvelope.offer);
    if (!this.inquiryId) {
      this.inquiryId = envelope.offer.policyInquiryVersion.id;
    }
    this.afterOfferChanged();

    if (this.offerEnvelope.offer.id) {
      this.getUserGroups();
    }
  }

  private afterOfferChanged() {
    this.serverErrors = undefined;
    this.showErrors = false;
    this.saveButton.hidden = !this.isEditable();
  }

  getInquiry() {
    const offer = this.offerEnvelope && this.offerEnvelope.offer;
    return getInquiryVersion(offer);
  }

  private isVotingStarted() {
    return this.offerEnvelope.offer.votes && this.offerEnvelope.offer.votes.length > 0;
  }

  voteDialog() {
    this.pending = false;
    this.votingDialog.open('voting.title');
  }

  vote(decision: string) {
    this.pending = true;
    this.voteCommentControl.setValue(this.voteCommentControl.value.trim());
    const voteComment = this.voteCommentControl.value;

    if (decision === 'down' && !voteComment) {
      const errors: {[key: string]: any} = {required: true};
      this.voteCommentControl.setErrors(errors);
      this.pending = false;
      return;
    }

    const vote: VoteDto = <VoteDto>{};
    vote.approve = decision === 'up';
    vote.comment = voteComment;

    this.inProgress = true;
    this.inquiryService
      .voteOffer(this.offerEnvelope.offer.id, vote)
      .pipe(tap(() => this.votingDialog.hide()))
      .subscribe(
        (offer) => this.offerChangedByOperation(offer, 'Vote saved!'),
        (error) => this.handleServerError(error),
        () => {
          this.inProgress = false;
          this.pending = false;
        }
      );
  }

  private offerChangedByOperation(offer, msg) {
    this.offerEnvelope.binding = this.computeBinding(offer);
    this.offerEnvelope.offer = offer;
    this.growlService.notice(msg);
    this.afterOfferChanged();
  }

  // After voting envelop is not refreshed. Field binding is calculated based on previo
  private computeBinding(offer) {
    if (this.offerEnvelope.binding === true || this.offerEnvelope.binding === false) {
      return this.offerEnvelope.binding;
    }

    const prevStatusId = this.offerEnvelope.offer.status.id;
    const isBState = (statusId) =>
      statusId === PolicyInquiryOfferStatus.BINDING || statusId === PolicyInquiryOfferStatus.NOT_BINDING;

    if (isBState(prevStatusId) && !isBState(offer.status.id)) {
      return prevStatusId === PolicyInquiryOfferStatus.BINDING;
    }
    return null;
  }

  canVote() {
    const hasRight =
      (this.offerEnvelope.offer.edge &&
        this.loggedUserService.hasRight(PolicyElementaryRight.POLICY_INQUIRY_EDGE_OFFER_DECISION)) ||
      (!this.offerEnvelope.offer.edge &&
        this.loggedUserService.hasRight(PolicyElementaryRight.POLICY_INQUIRY_CLIENT_OFFER_DECISION));
    return this.isStateToVote() && this.offerEnvelope.hasRightToVote && hasRight;
  }

  private isStateToVote() {
    return (
      this.offerEnvelope &&
      this.offerEnvelope.offer &&
      this.offerEnvelope.offer.votingStatus &&
      this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.PENDING &&
      !this.offerEnvelope.offer.policyContractVersion
    );
  }

  canSave() {
    return this.isEditable((offer) => !offer.blogOfferSource);
  }

  canSaveBlog() {
    return this.isEditable();
  }

  canChangeState() {
    const oe = this.offerEnvelope;
    return (
      oe &&
      oe.offer &&
      oe.offer.id &&
      !(
        oe.offer.blogOfferSource &&
        (oe.offer.status.id === PolicyInquiryOfferStatus.BINDING ||
          oe.offer.status.id === PolicyInquiryOfferStatus.NOT_BINDING)
      )
    );
  }

  public isEditable(extraCondition?: (o: PolicyInquiryOfferDto) => boolean) {
    const oe = this.offerEnvelope;
    return (
      oe &&
      oe.offer &&
      !this.isVotingStarted() &&
      oe.offer.votingStatus &&
      oe.offer.votingStatus.id !== PolicyInquiryOfferVotingStatus.ACCEPTED &&
      oe.offer.votingStatus.id !== PolicyInquiryOfferVotingStatus.REJECTED &&
      oe.offer.votingStatus.id !== PolicyInquiryOfferVotingStatus.POLICY_CREATED &&
      oe.offer.status.id !== PolicyInquiryOfferStatus.CLOSED &&
      (!extraCondition || extraCondition(oe.offer))
    );
  }

  createRightFunc(o: PolicyInquiryOfferDto): string {
    if (o) {
      return o.edge
        ? PolicyElementaryRight.POLICY_INQUIRY_EDGE_OFFER_CREATE
        : PolicyElementaryRight.POLICY_INQUIRY_CLIENT_OFFER_CREATE;
    }
    return undefined;
  }

  updateRightFunc(o: PolicyInquiryOfferDto): string {
    if (o) {
      return o.edge
        ? PolicyElementaryRight.POLICY_INQUIRY_EDGE_OFFER_UPDATE
        : PolicyElementaryRight.POLICY_INQUIRY_CLIENT_OFFER_UPDATE;
    }
    return undefined;
  }

  canCreatePolicy() {
    return (
      this.offerEnvelope &&
      this.offerEnvelope.offer &&
      this.offerEnvelope.offer.votingStatus &&
      this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.ACCEPTED &&
      this.offerEnvelope.offer.status &&
      this.offerEnvelope.offer.status.id === PolicyInquiryOfferStatus.ACCEPTED &&
      !this.offerEnvelope.offer.policyContractVersion &&
      !this.offerEnvelope.createdPolicy &&
      this.loggedUserService.hasRight(PolicyElementaryRight.POLICY_CREATE)
    );
  }

  policyCreationDataVisible() {
    return (
      this.offerEnvelope &&
      this.offerEnvelope.offer &&
      this.offerEnvelope.offer.votingStatus &&
      (this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.ACCEPTED ||
        this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.REJECTED ||
        this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.POLICY_CREATED)
    );
  }

  createPolicy() {
    this.inProgress = true;
    const offer = <PolicyCreationDataDto>{
      id: this.offerEnvelope.offer.id,
      questionnaireDate: this.offerEnvelope.offer.questionnaireDate,
    };
    this.inquiryService.createPolicyFromOffer(offer).subscribe({
      next: (creationInfo) => this.routerService.toPolicyContractPreview(creationInfo.id),
      error: (error) => this.handleServerError(error),
      complete: () => (this.inProgress = false),
    });
  }

  grcChanged(val: boolean) {
    if (!val) {
      this.offerEnvelope.offer.grcDate = undefined;
    }
  }

  sendToGroup(userGroup): void {
    this.offerEnvelope.offer.voting.userGroup = userGroup;
    this.onSave(true);
  }

  votingStatusAccepted(): boolean {
    return (
      this.offerEnvelope.offer.votingStatus &&
      (this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.ACCEPTED ||
        this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.POLICY_CREATED)
    );
  }

  votingStatusRejected(): boolean {
    return (
      this.offerEnvelope.offer.votingStatus &&
      this.offerEnvelope.offer.votingStatus.id === PolicyInquiryOfferVotingStatus.REJECTED
    );
  }

  onLifecycle() {
    this.sendOffer(false, (vt) => this.inquiryService.saveOffer(this.updateModel(), vt));
  }

  canBlogOffer() {
    return (
      this.offerEnvelope &&
      this.offerEnvelope.offer &&
      this.offerEnvelope.offer.status &&
      (this.offerEnvelope.offer.status.id === PolicyInquiryOfferStatus.ACCEPTED ||
        this.offerEnvelope.offer.status.id === PolicyInquiryOfferStatus.ACCEPTED_BY_CLIENT ||
        this.offerEnvelope.offer.status.id === PolicyInquiryOfferStatus.POLICY_CREATED_EXTERNALLY)
    );
  }

  createBlogOffer() {
    const offer = this.offerEnvelope.offer;
    const inq = offer.policyInquiryVersion;
    this.routerService.toNewInquiryOfferDetails(inq.id, inq.productType.id, offer.id, true);
  }

  canCloseVoting() {
    const hasRight =
      (this.offerEnvelope.offer.edge &&
        this.loggedUserService.hasRight(PolicyElementaryRight.POLICY_INQUIRY_EDGE_OFFER_DECISION)) ||
      (!this.offerEnvelope.offer.edge &&
        this.loggedUserService.hasRight(PolicyElementaryRight.POLICY_INQUIRY_CLIENT_OFFER_DECISION));
    return this.isStateToVote() && this.offerEnvelope.hasRightToCloseVoting && this.existsVotes() && hasRight;
  }

  private existsVotes() {
    const env = this.offerEnvelope;
    return env && env.offer && env.offer.votes && env.offer.votes.length > 0;
  }

  closeVoting() {
    this.inProgress = true;
    this.inquiryService.closeVoting(this.offerEnvelope.offer.id).subscribe({
      next: (offer) => this.offerChangedByOperation(offer, 'Voting closed!'),
      error: (error) => this.handleServerError(error),
      complete: () => (this.inProgress = false),
    });
  }

  goToOffer(offerId: number) {
    this.routerService.toInquiryOfferDetails(this.inquiryId, offerId);
  }

  filteredVotes(approve: boolean): VoteDto[] {
    if (this.offerEnvelope && this.offerEnvelope.offer && Array.isArray(this.offerEnvelope.offer.votes)) {
      return this.offerEnvelope.offer.votes.filter((v) => v.approve === approve);
    }
    return [];
  }

  isSectionVisible(viewSection: PolicyContractViewSection, defaultVisibility = false) {
    if (this.offerEnvelope && this.offerEnvelope.offer && this.offerEnvelope.offer.type) {
      return this.policyContractViewService.isSectionVisible(
        this.offerEnvelope.offer.type.id,
        viewSection,
        defaultVisibility
      );
    }
    return false;
  }

  requiredNql(): (control: AbstractControl) => ValidationErrors | null {
    return (control) => {
      if (this.isSectionVisible(this.viewSection.OFFER__NQL)) {
        return Validators.required(control);
      }
      return null;
    };
  }

  getVotingLabel() {
    return (
      this.offerEnvelope.offer.voting.userGroup?.name +
      ' ' +
      this.translateService.instant('offer.voting.votingStartDate')
    );
  }

  getOfferTypeLabel() {
    return this.translateService.instant('offer.offerType') + ': ' + this.offerEnvelope.offer.type?.name;
  }

  globalLOLAmount(): boolean {
    return (
      this.offerEnvelope.offer.globalLOLType && this.offerEnvelope.offer.globalLOLType.id === FieldValueType.AMOUNT
    );
  }

  globalLOLTypeChanged() {
    if (this.globalLOLAmount()) {
      this.offerEnvelope.offer.globalLOLMultiplicity = undefined;
    } else {
      this.offerEnvelope.offer.globalLOL = undefined;
      this.offerEnvelope.offer.globalLOLCurrency = undefined;
    }
  }

  discretionaryCoverChanged(val: boolean) {
    if (!val) {
      this.offerEnvelope.offer.dcMaxPaymentTermDays = undefined;
      this.offerEnvelope.offer.dcMaxLimit = undefined;
      this.offerEnvelope.offer.dcInsuranceCoverPercent = undefined;
      this.offerEnvelope.offer.dcInsuredAmount = undefined;
    }
  }

  showSendToButton() {
    const userGroupCond = !this.offerEnvelope.offer.voting?.userGroup;
    return userGroupCond;
  }

  onInquiryTypeChanged() {
    this.bonusMalusSectionLabelKey = 'offer.details.discretionaryCoverBonusMalus.title';
  }

  onRiskOptionChanged(value: BooleanPair) {
    this.offerEnvelope.offer.politicalRisk = value.left;
    this.offerEnvelope.offer.commercialRisk = value.right;
  }

  createNewPolicy(key: string) {
    switch (key) {
      case 'POLICY_CREATED':
        this.createPolicy();
        break;
      case 'POLICY_CREATED_EXTERNALLY':
        if (this.offerService instanceof OfferService) {
          this.inquiryService.markCreatedExternally(this.offerEnvelope.offer.id).subscribe({
            next: (pcv) => {
              this.setOffer(pcv);
              this.afterObjectSaved();
            },
            error: (error) => this.handleServerError(error),
          });
        }
        break;
      default:
        console.log('unknown action');
    }
  }

  setOffer(inV: PolicyInquiryOfferEnvelopeDto) {
    console.log('set offer: ', inV);
    this.offerEnvelope = inV;
  }
}
